// Shared by the browser and the Node renderer. Authored descriptions always
// win; the fallback describes source copy or the verified stock, not promises.
const EMPTY = {
  en: "No matching vehicles are currently listed.",
  da: "Der er ingen matchende biler lige nu.",
  de: "Derzeit sind keine passenden Fahrzeuge gelistet.",
  es: "Actualmente no hay vehículos que coincidan.",
  fr: "Aucun véhicule correspondant n'est actuellement proposé.",
  it: "Al momento non sono disponibili veicoli corrispondenti.",
  nl: "Er zijn momenteel geen passende voertuigen.",
  no: "Ingen matchende biler er tilgjengelige akkurat nå.",
  nb: "Ingen matchende biler er tilgjengelige akkurat nå.",
  sv: "Det finns inga matchande bilar just nu.",
  pt: "Não existem veículos correspondentes neste momento.",
  fi: "Vastaavia ajoneuvoja ei ole tällä hetkellä saatavilla.",
  pl: "Obecnie nie ma pasujących pojazdów.",
  sk: "Momentálne nie sú uvedené žiadne zodpovedajúce vozidlá.",
  bg: "В момента няма подходящи автомобили.",
};

function plainText(value) {
  return String(value || "").replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"').replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, " ").trim();
}

export function landingCatalogueDescription(page, inventory, bodyText = "") {
  const authored = page?.seo_meta_description || page?.page_description;
  if (typeof authored === "string" && authored.trim()) return authored;
  const body = plainText(bodyText);
  if (body.length >= 24) return body.slice(0, 200);
  const heading = plainText(page?.hero_heading || page?.name || page?.page_title);
  const cohort = inventory?.cohorts?.find((item) => item.key === inventory.defaultCohort);
  const cars = cohort?.cars;
  if (inventory?.state !== "ready" || !Array.isArray(cars)) return heading;
  if (!cars.length) {
    const language = String(page?.language || "en").toLowerCase().split("-")[0];
    return `${heading}. ${EMPTY[language] || EMPTY.en}`;
  }
  const facts = cars.slice(0, 3).map((car) => [
    [car.year, car.make, car.model].filter(Boolean).join(" "),
    [car.city, car.state, car.countryCode].filter(Boolean).join(", "),
  ].filter(Boolean).join(" — ")).join("; ");
  return `${heading}. ${facts}.`.slice(0, 200);
}

export function landingCountryWideStockLabel(page, inventory) {
  if (page?.page_type !== "city" || inventory?.state !== "ready") return null;
  const filters = page.filter_params || {};
  const inputs = page.cluster_inputs || {};
  if ([filters.city, filters.region, filters.state, filters.location, inputs.city, inputs.region]
    .some((value) => typeof value === "string" && value.trim())) return null;
  const country = inventory.countryCode;
  if (!/^[A-Z]{2}$/.test(country || "")) return null;
  const language = String(page.language || "en").toLowerCase().split("-")[0];
  let name = country;
  try {
    name = new Intl.DisplayNames([language], { type: "region" }).of(country) || country;
  } catch { /* ISO country code remains factual if a locale is unsupported. */ }
  const labels = {
    en: `Broader discovery across ${name} — these cars are not confirmed local matches.`,
    da: `Biler fra hele ${name} — disse biler er ikke bekræftede lokale resultater.`,
    de: `Fahrzeuge aus ${name} — diese Fahrzeuge sind keine bestätigten lokalen Treffer.`,
    es: `Vehículos de ${name} — no son resultados locales confirmados.`,
    fr: `Véhicules dans ${name} — il ne s'agit pas de résultats locaux confirmés.`,
    it: `Veicoli in ${name} — non sono risultati locali confermati.`,
    nl: `Auto's in ${name} — dit zijn geen bevestigde lokale resultaten.`,
    no: `Biler i ${name} — dette er ikke bekreftede lokale treff.`,
    nb: `Biler i ${name} — dette er ikke bekreftede lokale treff.`,
    sv: `Bilar i ${name} — detta är inte bekräftade lokala träffar.`,
    pt: `Veículos em ${name} — não são resultados locais confirmados.`,
    fi: `Ajoneuvoja maassa ${name} — nämä eivät ole vahvistettuja paikallisia tuloksia.`,
    pl: `Pojazdy w ${name} — nie są to potwierdzone wyniki lokalne.`,
    sk: `Vozidlá v ${name} — nejde o potvrdené miestne výsledky.`,
    bg: `Автомобили в ${name} — това не са потвърдени местни резултати.`,
  };
  return labels[language] || labels.en;
}